import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  StockAlert,
  StockAlertCreate,
  StockAlertUpdate,
  StockAlertResolutionRequest
} from '../models/StockAlert';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';
import { PagedResult, PaginationOptions } from '../shared/interfaces/pagination.interface';

export type StockAlertPagedResult = PagedResult<StockAlert>;

@Injectable({
  providedIn: 'root'
})
export class StockAlertsService {
  private get baseUrl() { return `${environment.microservices.stock}`; }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // ===== CRUD OPERATIONS =====

  getAll(): Observable<StockAlert[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock-alerts`).pipe(
      map(list => this.mapListFromDto(list || [])),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar alertas de estoque');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<StockAlert> {
    return this.http.get<any>(`${this.baseUrl}/stock-alert/${id}`).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar alerta de estoque'); return throwError(() => err); })
    );
  }

  create(payload: StockAlertCreate): Observable<StockAlert> {
    const dto = this.mapToDto(payload);
    return this.http.post<any>(`${this.baseUrl}/stock-alerts`, dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao criar alerta de estoque'); return throwError(() => err); })
    );
  }

  update(id: number, payload: StockAlertUpdate): Observable<StockAlert> {
    const dto = this.mapToDto(payload);
    return this.http.put<any>(`${this.baseUrl}/stock-alerts/${id}`, dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao atualizar alerta de estoque'); return throwError(() => err); })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/stock-alerts/${id}`).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao excluir alerta de estoque'); return throwError(() => err); })
    );
  }

  // ===== SPECIFIC QUERIES =====

  getUnresolved(options?: PaginationOptions): Observable<StockAlertPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/unresolved-stock-alerts`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar alertas pendentes'); return throwError(() => err); })
    );
  }

  getByType(type: string, options?: PaginationOptions): Observable<StockAlertPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/stock-alerts/type/${encodeURIComponent(type)}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar alertas por tipo'); return throwError(() => err); })
    );
  }

  getByItem(itemId: number, options?: PaginationOptions): Observable<StockAlertPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/stock-alerts/item/${itemId}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar alertas por item'); return throwError(() => err); })
    );
  }

  getByPeriod(start: string, end: string, options?: PaginationOptions): Observable<StockAlertPagedResult> {
    let params = new HttpParams()
      .set('start', start)
      .set('end', end);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/stock-alerts/period`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar alertas por período'); return throwError(() => err); })
    );
  }

  // ===== OPERATIONS =====

  resolveAlert(id: number, request: StockAlertResolutionRequest): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.baseUrl}/stock-alerts/${id}/resolve`, request).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao resolver alerta de estoque'); return throwError(() => err); })
    );
  }

  // ===== MAPPING METHODS =====

  private mapFromDto(dto: any): StockAlert {
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    const toIso = (d: any) => (d ? new Date(d).toISOString() : undefined);

    return {
      id: pick(dto, ['Id', 'id']) ?? 0,
      itemId: pick(dto, ['ItemId', 'itemId']) ?? 0,
      alertType: pick(dto, ['AlertType', 'alertType']) ?? 'LowStock',
      message: pick(dto, ['Message', 'message']) ?? '',
      alertDate: toIso(pick(dto, ['AlertDate', 'alertDate'])) ?? new Date().toISOString(),
      resolved: pick(dto, ['Resolved', 'resolved']) ?? false,
      resolutionDate: toIso(pick(dto, ['ResolutionDate', 'resolutionDate'])),
      resolutionNotes: pick(dto, ['ResolutionNotes', 'resolutionNotes']),
      createdAt: toIso(pick(dto, ['CreatedAt', 'createdAt'])),
      updatedAt: toIso(pick(dto, ['UpdatedAt', 'updatedAt'])),
      createdBy: pick(dto, ['CreatedBy', 'createdBy']),
      updatedBy: pick(dto, ['UpdatedBy', 'updatedBy']),

      // Dados relacionados
      itemCode: pick(dto, ['ItemCode', 'itemCode']),
      itemName: pick(dto, ['ItemName', 'itemName']),
      itemCurrentQuantity: pick(dto, ['ItemCurrentQuantity', 'itemCurrentQuantity']),
      itemMinimumQuantity: pick(dto, ['ItemMinimumQuantity', 'itemMinimumQuantity']),
      itemMaximumQuantity: pick(dto, ['ItemMaximumQuantity', 'itemMaximumQuantity']),
      warehouseName: pick(dto, ['WarehouseName', 'warehouseName']),
      staffName: pick(dto, ['StaffName', 'staffName'])
    };
  }

  private mapListFromDto(list: any[]): StockAlert[] {
    if (!list || !Array.isArray(list)) return [];
    return list.map(x => this.mapFromDto(x));
  }

  private mapToDto(payload: Partial<StockAlertCreate | StockAlertUpdate>): any {
    return {
      ItemId: payload.itemId,
      AlertType: payload.alertType,
      Message: payload.message,
      AlertDate: payload.alertDate ? new Date(payload.alertDate).toISOString() : null,
      Resolved: payload.resolved,
      ResolutionDate: payload.resolutionDate ? new Date(payload.resolutionDate).toISOString() : null,
      ResolutionNotes: payload.resolutionNotes
    };
  }

  private mapPagedResponse(response: any): StockAlertPagedResult {
    const items = Array.isArray(response?.Items) ? response.Items : Array.isArray(response?.items) ? response.items : [];
    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return { items: this.mapListFromDto(items), page, pageSize, totalItems, totalPages };
  }
}
